import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MapPin, Mail, ArrowUp } from "lucide-react";
import { contact, site } from "../mock";
import { useLang, LANGS } from "../i18n";
import Logo, { LogoMark } from "./Logo";
import { WhatsAppIcon, WhatsAppButton, waLink } from "./WhatsApp";

function LangSwitch({ light = false }) {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`inline-flex items-center rounded-full p-0.5 ${
        light ? "bg-white/10" : "bg-[#f1f2f4]"
      }`}
    >
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          aria-label={l.name}
          aria-pressed={lang === l.code}
          className={`rounded-full px-3 py-1 text-[12px] font-bold tracking-wide transition-colors duration-300 ${
            lang === l.code
              ? "bg-[#2a2b2f] text-white"
              : light
              ? "text-white/70 hover:text-white"
              : "text-slate-500 hover:text-[#2a2b2f]"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export function TopBar() {
  return (
    <div className="hidden md:block bg-[#2a2b2f] text-white/90 text-[13px]">
      <div className="mx-auto max-w-7xl px-6 flex items-center justify-between h-10">
        <div className="flex items-center gap-6">
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-[#d9b64e]" />
            {contact.street}, {contact.zipCity}
          </span>
          <span className="inline-flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-[#d9b64e]" />
            <a className="hover:text-white transition-colors" href={`mailto:${contact.email}`}>
              {contact.email}
            </a>
          </span>
        </div>
        <a
          href={`tel:${contact.phoneLink}`}
          className="inline-flex items-center gap-2 font-medium hover:text-white transition-colors"
        >
          <Phone className="h-3.5 w-3.5 text-[#d9b64e]" />
          {contact.phoneDisplay}
        </a>
      </div>
    </div>
  );
}

export function Navbar() {
  const location = useLocation();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("start");
  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(null);

  // Hoehe der fixierten Kopfleiste messen -> Platzhalter + scroll-padding
  useEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;

    const update = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (!h) return;
      setBarHeight((prev) => (prev === h ? prev : h));
      document.documentElement.style.setProperty("--header-h", `${h}px`);
    };

    update();
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    let frame = null;

    const measure = () => {
      frame = null;
      setScrolled(window.scrollY > 24);

      const offsets = t.nav
        .map((l) => {
          const el = document.getElementById(l.id);
          if (!el) return null;
          return { id: l.id, top: el.getBoundingClientRect().top };
        })
        .filter(Boolean);

      if (!offsets.length) {
        setActive("");
        return;
      }

      const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 120;
      const current = offsets.filter((o) => o.top <= window.innerHeight * 0.45).pop();
      const next = atBottom ? t.nav[t.nav.length - 1].id : current ? current.id : null;
      if (next) setActive((prev) => (prev === next ? prev : next));
    };

    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [location.pathname, t]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <div ref={barRef} className="fixed inset-x-0 top-0 z-50">
        <TopBar />
        <header
          className={`bg-white/95 backdrop-blur-xl [transition:box-shadow_0.3s_ease] ${
            scrolled ? "shadow-[0_8px_30px_-18px_rgba(42,43,47,0.45)]" : "shadow-none"
          }`}
        >
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex h-[76px] items-center justify-between lg:h-[92px]">
            <a href="/#start" className="group flex items-center">
              <Logo />
            </a>

            <nav className="hidden xl:flex items-center gap-7">
              {t.nav.map((l) => (
                <a
                  key={l.id}
                  href={`/#${l.id}`}
                  className={`nav-link text-[14px] font-medium ${
                    active === l.id ? "active text-[#2a2b2f]" : "text-slate-600 hover:text-[#2a2b2f]"
                  }`}
                >
                  {l.label}
                </a>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <LangSwitch />
              <a
                href="/#kontakt"
                className="hidden sm:inline-flex btn-primary rounded-full px-5 py-2.5 text-sm font-semibold"
              >
                {t.navCta}
              </a>
              <button
                type="button"
                aria-label={t.menuOpen}
                onClick={() => setOpen(true)}
                className="xl:hidden grid h-11 w-11 place-items-center rounded-xl border border-slate-200 text-[#2a2b2f] hover:bg-slate-50 transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
          <div className="hairline" />
        </header>
      </div>

      {/* Platzhalter in der Hoehe der fixierten Kopfleiste */}
      <div
        aria-hidden="true"
        className={barHeight ? "" : "h-[76px] md:h-[112px] lg:h-[132px]"}
        style={barHeight ? { height: `${barHeight}px` } : undefined}
      />

      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 z-[60] overflow-hidden xl:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none invisible"
        }`}
      >
        <div
          className="absolute inset-0 bg-[#1c1d20]/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
        <div
          className={`absolute right-0 top-0 h-full w-[86%] max-w-sm overflow-y-auto bg-white shadow-2xl transition-transform duration-300 ease-out ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between h-[76px] px-6 border-b border-slate-100">
            <Logo className="h-12" />
            <button
              type="button"
              aria-label={t.menuClose}
              onClick={() => setOpen(false)}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-slate-200 text-[#2a2b2f]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex flex-col p-6 gap-1">
            {t.nav.map((l) => (
              <a
                key={l.id}
                href={`/#${l.id}`}
                onClick={() => setOpen(false)}
                className="rounded-xl px-4 py-3.5 text-[15px] font-medium text-slate-700 hover:bg-[#f1f2f4] hover:text-[#2a2b2f] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="px-6 pb-10 space-y-3">
            <WhatsAppButton full />
            <a
              href={`tel:${contact.phoneLink}`}
              className="flex items-center justify-center gap-2 btn-primary rounded-full px-5 py-3 text-sm font-semibold"
            >
              <Phone className="h-4 w-4" /> {t.navCall}
            </a>
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center justify-center gap-2 btn-outline rounded-full px-5 py-3 text-sm font-semibold"
            >
              <Mail className="h-4 w-4" /> {t.navMail}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

export function Footer() {
  const { t } = useLang();
  return (
    <footer className="relative bg-[#1c1d20] text-white/75 grain overflow-hidden">
      <div className="relative mx-auto max-w-7xl px-6 py-16">
        <div className="grid gap-12 md:grid-cols-4">
          <div className="md:col-span-2">
            <LogoMark className="h-24 lg:h-28" />
            <p className="mt-5 max-w-md text-[15px] leading-relaxed">{t.footer.text}</p>
            <p className="mt-5 text-sm text-[#d9b64e]">{t.footer.highlight}</p>
            <div className="mt-6">
              <LangSwitch light />
            </div>
          </div>

          <div>
            <h4 className="eyebrow text-[#d9b64e]">{t.footer.navTitle}</h4>
            <ul className="mt-5 space-y-2.5 text-[15px]">
              {t.nav.map((l) => (
                <li key={l.id}>
                  <a href={`/#${l.id}`} className="hover:text-white transition-colors">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="eyebrow text-[#d9b64e]">{t.footer.contactTitle}</h4>
            <ul className="mt-5 space-y-3 text-[15px]">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-1 text-[#d9b64e] shrink-0" />
                <span>
                  {contact.street}
                  <br />
                  {contact.zipCity}
                </span>
              </li>
              <li className="flex gap-3">
                <Phone className="h-4 w-4 mt-1 text-[#d9b64e] shrink-0" />
                <a href={`tel:${contact.phoneLink}`} className="hover:text-white transition-colors">
                  {contact.phoneDisplay}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="h-4 w-4 mt-1 text-[#d9b64e] shrink-0" />
                <a
                  href={`mailto:${contact.email}`}
                  className="hover:text-white transition-colors break-all"
                >
                  {contact.email}
                </a>
              </li>
              <li className="flex gap-3">
                <WhatsAppIcon className="h-4 w-4 mt-1 text-[#d9b64e] shrink-0" />
                <a
                  href={waLink(t.whatsapp.prefill)}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-white transition-colors"
                >
                  {t.whatsapp.short}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-7">
          <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
            {t.legalLinks.map((l) => (
              <Link key={l.to} to={l.to} className="text-[13.5px] hover:text-white transition-colors">
                {l.label}
              </Link>
            ))}
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[13px]">
              © {new Date().getFullYear()} {site.name}
            </p>
            <a
              href="/#start"
              className="inline-flex items-center gap-2 text-[13px] hover:text-white transition-colors"
            >
              {t.footer.top} <ArrowUp className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
